import React from "react";
import { BackgroundColorI, StrapiImage, TitleType } from "../Types";
import CalculatorModuleExample from "../CalculatorModule/CalculatorExample";

import { getTitleHTMLTag } from "../../helpers/tools";
import GlobalIcon from "../Icons/GlobalIcon";
import Typography from "get-life-storybook-ts/lib/atoms/Typography/Typography";
import TrustPilotDS from "../TrustPilot/TrustPilotDS";
import CalculatorModuleIT from "../CalculatorModuleIT/CalculatorModuleIT";
import { fixStrapiUrl } from "../../helpers/strapiUrl";
import ResponsiveImage from "../Images/ResponsiveImage";

interface HeroCalculatorImageI {
  data: {
    BackgroundColor: BackgroundColorI;
    CalculatorModule: {
      Url: string;
      Promo?: string;
    }[];
    TrustpilotScore: string;
    DesktopImage?: StrapiImage;
    DesktopImageURL?: string;
    DesktopImageAlt: string;
    HasArrow: boolean;
    HasSerifTitle: boolean;
    HasSubtitle: boolean;
    HasTrustpilotRating: boolean;
    HeroSansTitle: string;
    HeroSerifTitle: string;
    HeroTitleHeading: {
      TitleTypeSelection: TitleType;
    };
    MobileImage?: StrapiImage;
    MobileImageURL?: string;
    MobileImageAlt: string;
    ExcellentText: string;
    Subtitle: string;
    SubtitleHeading: {
      TitleTypeSelection: TitleType;
    };
    ImageHasRadius: boolean;
    ImageSize: "Large" | "Medium";
    TrustpilotLink?: string;
    HasPhoneValidation?: boolean;
    HasSeriousIllnesses?: boolean;
    HasLogo: boolean;
    DesktopLogoAlt?: string;
    MobileLogoAlt?: string;
    MobileLogo?: StrapiImage;
    DesktopLogo?: StrapiImage;
    DesktopLogoUrl?: string;
    MobileLogoUrl?: string;
  };
}

const HeroCalculatorImage = ({ data }: HeroCalculatorImageI) => {
  const {
    BackgroundColor,
    TrustpilotScore,
    DesktopImage,
    DesktopImageURL,
    DesktopImageAlt,
    HasArrow,
    HasSerifTitle,
    HasSubtitle,
    HasTrustpilotRating,
    HeroSansTitle,
    HeroSerifTitle,
    HeroTitleHeading,
    MobileImage,
    MobileImageURL,
    MobileImageAlt,
    ExcellentText,
    Subtitle,
    SubtitleHeading,
    ImageHasRadius,
    ImageSize,
    TrustpilotLink,
    HasPhoneValidation,
    HasSeriousIllnesses,
    HasLogo,
    DesktopLogoAlt,
    MobileLogoAlt,
    MobileLogo,
    DesktopLogo,
    DesktopLogoUrl,
    MobileLogoUrl,
  } = data;

  const DOMAIN = process.env.GATSBY_APP_LOCALE;
  
  const backgroundColor = {
    Blue_Gray: "#F5F7FA",
    Light_Blue: "#ECF0FF",
    White: "#FFFFFF",
  }[BackgroundColor.BackgroundColor || "White"];

  const calculatorBackgroundColor = {
    Blue_Gray: "#FFFFFF",
    Light_Blue: "#F5F7FA",
    White: "#F5F7FA",
  }[BackgroundColor.BackgroundColor || "White"];

  const imageHeight = {
    Large: "965px",
    Medium: "520px",
  }[ImageSize];

  const imageOffset = {
    Large: "-24px",
    Medium: "0px",
  }[ImageSize];

  // Phase 5 (responsive images). The desktop and mobile copies of the hero are
  // normally the SAME file: the home emitted it twice, both fetchPriority="high",
  // differing only by `hidden md:block` / `md:hidden`. When they are identical we
  // render ONE <img> with `sizes` so the browser picks the right variant at each
  // breakpoint; when a page really does use two different files we keep the two
  // elements, and their visibility classes, exactly as before.
  const desktopSrc = DesktopImageURL || (DesktopImage?.url ? fixStrapiUrl(DesktopImage.url) : "");
  const mobileSrc = MobileImageURL || (MobileImage?.url ? fixStrapiUrl(MobileImage.url) : "");
  const singleHeroImage = !!desktopSrc && desktopSrc === mobileSrc;

  const heroImageClass = (visibility: string) =>
    [
      "h-full absolute w-[100vw] md:w-[50vw] overflow-hidden",
      visibility,
      "max-w-none object-cover object-left",
      ImageHasRadius ? "rounded-s-full" : "",
    ]
      .filter(Boolean)
      .join(" ");

  const prepareDataForCalculator = () => {
    return data?.CalculatorModule.map((cm) => {
      return {
        Url: cm.Url,
        Promo: cm.Promo,
        backgroundColor: calculatorBackgroundColor,
        HasPhoneValidation: false,
        HasSeriousIllnesses: HasSeriousIllnesses
      };
    });
  };

  let titleC = getTitleHTMLTag(
    <>
      <span className="font-DM font-bold md:text-[48px] text-[32px] md:leading-[48px] leading-[41.66px] tracking-[-0.02em]">
        {HeroSansTitle}
      </span>
      {HasSerifTitle ? (
        <span className="font-MW font-light md:text-[45px] text-[30px] md:leading-[56.57px] leading-[37.71px] tracking-[-0.02em]">
          {HeroSerifTitle}
        </span>
      ) : null}
    </>,
    HeroTitleHeading?.TitleTypeSelection,
    "text-[#424242] flex flex-col"
  );

  let subtitleC = getTitleHTMLTag(
    Subtitle,
    SubtitleHeading?.TitleTypeSelection,
    "text-[var(--primary-color)] font-medium md:text-[22px] text-[18px] md:leading-[28.64px] leading-[23.44px] tracking-[-0.02em] md:mt-[16px] mt-[8px]"
  );

  return (
    <div className="overflow-hidden" style={{ backgroundColor }}>
      <div className="grid md:grid-cols-12 gap-[32px] items-center max-w-content mx-auto py-[80px] xl:px-[0px] px-[24px]">
        <div className="md:col-span-5 max-w-[448px] md:max-w-none relative">
          {HasLogo ? (
            DesktopLogoUrl ? (
              <img
                src={DesktopLogoUrl}
                className={"mb-[20px] w-fit hidden md:block"}
                alt={DesktopLogoUrl}
              />
            ) : DesktopLogo?.url ? (
              <img
                src={fixStrapiUrl(DesktopLogo?.url)}
                width={DesktopLogo?.width}
                height={DesktopLogo?.height}
                className={"mb-[20px] w-fit hidden md:block"}
                alt={DesktopLogoAlt}
              />
            ) : null
          ) : null}
          {HasLogo ? (
            MobileLogoUrl ? (
              <img
                src={MobileLogoUrl}
                className={"mb-[20px] w-fit md:hidden"}
                alt={MobileLogoAlt}
              />
            ) : MobileLogo?.url ? (
              <img
                src={fixStrapiUrl(MobileLogo?.url)}
                width={MobileLogo?.width}
                height={MobileLogo?.height}
                className={"mb-[20px] w-fit md:hidden"}
                alt={MobileLogoAlt}
              />
            ) : null
          ) : null}
          <div className="mb-[24px]">
            {titleC}
            {HasSubtitle ? subtitleC : null}
          </div>
          {DOMAIN === "it" ? (
                  <CalculatorModuleIT data={prepareDataForCalculator()} />
                ) : (<CalculatorModuleExample data={prepareDataForCalculator()} />)}
          {HasTrustpilotRating ? (
            <TrustPilotDS
              CurrentRateLabel={TrustpilotScore}
              OutOfLabel={ExcellentText}
              className="text-center mt-[32px] w-full"
              TrustpilotLink={TrustpilotLink}
            />
          ) : null}
          {HasArrow ? (
            <GlobalIcon
              iconName="LoopingArrow"
              className="hidden md:block [&>svg]:w-[303px] [&>svg]:h-[303px] absolute bottom-[80px] right-[-270px] z-[1]"
            />
          ) : null}
        </div>
        <div
          className={`heroCalculatorImage_ImageHeight md:col-start-7 md:col-end-13 relative`}
          style={
            {
              "--heroCalculatorImageHeight": imageHeight,
              "--heroCalculatorImageOffset": imageOffset,
            } as React.CSSProperties
          }
        >
          {singleHeroImage ? (
            <ResponsiveImage
              className={heroImageClass("")}
              src={desktopSrc}
              alt={DesktopImageAlt || MobileImageAlt}
              sizes="(min-width:768px) 50vw, 100vw"
              fetchPriority="high"
              width={1858}
              height={1040}
            />
          ) : (
            <>
              {desktopSrc ? (
                <ResponsiveImage
                  className={heroImageClass("hidden md:block")}
                  src={desktopSrc}
                  alt={DesktopImageAlt}
                  sizes="50vw"
                  fetchPriority="high"
                  width={1858}
                  height={1040}
                />
              ) : null}
              {mobileSrc ? (
                <ResponsiveImage
                  className={heroImageClass("md:hidden")}
                  src={mobileSrc}
                  alt={MobileImageAlt}
                  sizes="100vw"
                  fetchPriority="high"
                  width={1000}
                  height={600}
                />
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeroCalculatorImage;
